/**
 * The single seam between the app and Telegram's WebApp bridge.
 *
 * Everything here degrades to a no-op outside Telegram so the kitchen sink, Playwright and a
 * plain browser tab all render the real components rather than a fallback.
 */

export function twa() {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
}

/** Telegram version gate — several APIs we use only exist from Bot API 7.x/8.x. */
export function supports(minVersion: string): boolean {
  const v = twa()?.version;
  if (!v) return false;
  const a = v.split('.').map(Number);
  const b = minVersion.split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return true;
}

/**
 * Resolve a `--pv-*` token to the concrete hex Telegram's chrome APIs require.
 *
 * Returns undefined rather than guessing when the token can't be resolved, so callers skip
 * the native call and let Telegram keep its own default — inventing a colour here would be
 * the one thing that survives a theme switch unchanged.
 */
export function tokenColor(token: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  if (!raw) return undefined;
  // Telegram only accepts #rgb/#rrggbb. color-mix() results come back as rgb(); convert.
  if (raw.startsWith('#')) return raw;
  const m = raw.match(/rgba?\(([^)]+)\)/);
  if (!m) return undefined;
  const [r, g, b] = m[1]!
    .split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);
  if ([r, g, b].some((n) => !Number.isFinite(n))) return undefined;
  const hex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r!)}${hex(g!)}${hex(b!)}`;
}

/**
 * Paint Telegram's own header/background/bottom-bar to match the screen underneath, so the
 * app reads as one continuous surface instead of a webview pasted into a frame.
 */
export function syncChrome(
  opts: { header?: 'surface' | 'page'; bottomBar?: 'surface' | 'page' } = {},
) {
  const w = twa();
  if (!w) return;
  const header = tokenColor(opts.header === 'page' ? '--pv-bg-secondary' : '--pv-surface');
  const page = tokenColor('--pv-bg-secondary');
  const bottom = tokenColor(opts.bottomBar === 'page' ? '--pv-bg-secondary' : '--pv-surface');
  try {
    if (header) w.setHeaderColor(header);
    if (page) w.setBackgroundColor(page);
    if (bottom && supports('7.10')) w.setBottomBarColor?.(bottom);
  } catch {
    /* older clients reject unknown colours — the webview still renders correctly */
  }
}

/** Mirror Telegram's safe-area insets onto CSS vars (Bot API 8.0+; env() covers the rest). */
export function syncSafeArea() {
  const w = twa();
  if (!w) return;
  const root = document.documentElement;
  const content = w.contentSafeAreaInset;
  const device = w.safeAreaInset;
  const top = (content?.top ?? 0) + (device?.top ?? 0);
  const bottom = (content?.bottom ?? 0) + (device?.bottom ?? 0);
  if (top > 0) root.style.setProperty('--pv-safe-top', `${top}px`);
  if (bottom > 0) root.style.setProperty('--pv-safe-bottom', `${bottom}px`);
}

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

export function haptic(style: HapticStyle = 'light') {
  try {
    twa()?.HapticFeedback.impactOccurred(style);
  } catch {
    /* unsupported client */
  }
}

export function hapticNotify(type: 'success' | 'error' | 'warning' = 'success') {
  try {
    twa()?.HapticFeedback.notificationOccurred(type);
  } catch {
    /* unsupported client */
  }
}

export function hapticSelection() {
  try {
    twa()?.HapticFeedback.selectionChanged();
  } catch {
    /* unsupported client */
  }
}

export function openTelegramLink(url: string) {
  const w = twa();
  if (w) w.openTelegramLink(url);
  else window.open(url, '_blank', 'noopener');
}

export function openLink(url: string) {
  const w = twa();
  if (w) w.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Language Telegram reports for the user; drives dir + copy. */
export function telegramLanguage(): string {
  return twa()?.initDataUnsafe?.user?.language_code ?? navigator.language?.slice(0, 2) ?? 'en';
}

export function telegramUser() {
  return twa()?.initDataUnsafe?.user;
}

/**
 * Deep-link payload — read from all three places Telegram can put it.
 *
 * The app is reachable by two different mechanisms and they deliver the payload differently:
 *
 * 1. **`t.me/<bot>?startapp=prompt_<id>`** (channel posts, share links). Telegram launches the
 *    Main Mini App with `#tgWebAppStartParam=…` on the URL, which `telegram-web-app.js` copies
 *    into `initDataUnsafe.start_param`.
 * 2. **A `web_app` keyboard button** whose URL we build ourselves, as the bot card does. Telegram
 *    opens that URL verbatim and sets **no** `start_param` at all — the payload only exists as the
 *    `?startapp=` we put there, so reading `initDataUnsafe` alone finds nothing and the app opens
 *    on Home instead of on the prompt the user tapped.
 *
 * So the URL is consulted as well as `initDataUnsafe`, and both hash and query are checked because
 * Telegram uses the fragment while our own `web_app` URLs use the query string.
 */
export function startParam(): string | null {
  const fromInitData = twa()?.initDataUnsafe?.start_param;
  if (fromInitData) return fromInitData;

  for (const source of [window.location.search, window.location.hash]) {
    if (!source) continue;
    const params = new URLSearchParams(source.replace(/^[?#]/, ''));
    const value = params.get('tgWebAppStartParam') ?? params.get('startapp');
    if (value) return value;
  }
  return null;
}
